// @ts-nocheck
/**
 * ROI Visualization Component
 * Carbon Charts integration for displaying calculator results
 */

import React, { useMemo } from 'react';
import { SimpleBarChart, LineChart, DonutChart, ComboChart } from '@carbon/charts-react';
import {
  Grid,
  Column,
  Tile,
  Tag,
  Button,
  Stack,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
} from '@carbon/react';
import { Download, Share, Analytics } from '@carbon/icons-react';
import type { ROICalculatorResults } from '../../../../shared/interfaces/EducationalCalculators';
import '@carbon/charts-react/styles.css';

interface ROIVisualizationProps {
  results: ROICalculatorResults;
  variant?: 'detailed' | 'summary' | 'comparison';
  onExportPDF?: () => void;
  onShare?: () => void;
}

export const ROIVisualization: React.FC<ROIVisualizationProps> = ({
  results,
  variant = 'detailed',
  onExportPDF,
  onShare,
}) => {
  // Data for Cost Comparison Bar Chart
  const costComparisonData = useMemo(
    () => [
      {
        group: 'Traditional Tutoring',
        value: results.comparisonToTraditionalTutoring.savings + results.totalInvestment,
      },
      {
        group: 'Sigma08 Platform',
        value: results.totalInvestment,
      },
      {
        group: 'Savings',
        value: results.comparisonToTraditionalTutoring.savings,
      },
    ],
    [results]
  );

  // Data for ROI Growth Line Chart (5-year projection)
  const roiGrowthData = useMemo(() => {
    const data = [];
    const initialValue = results.yearlyValue;
    for (let year = 1; year <= 5; year++) {
      data.push({
        date: `Year ${year}`,
        value: Math.round(initialValue * year * (1 + year * 0.1)),
      });
    }
    return data;
  }, [results.yearlyValue]);

  // Data for Value Breakdown Donut Chart
  const valueBreakdownData = useMemo(
    () => [
      {
        group: 'Direct Cost Savings',
        value: results.comparisonToTraditionalTutoring.savings,
      },
      {
        group: 'Academic Improvement Value',
        value: results.expectedGradeImprovement * 1000,
      },
      {
        group: 'Time Efficiency Value',
        value:
          results.yearlyValue -
          results.comparisonToTraditionalTutoring.savings -
          results.expectedGradeImprovement * 1000,
      },
    ],
    [results]
  );

  // Data for Detailed Comparison Table
  const comparisonTableData = useMemo(
    () => [
      {
        id: '1',
        metric: 'Annual Investment',
        traditional: `$${(results.comparisonToTraditionalTutoring.savings + results.totalInvestment).toLocaleString()}`,
        sigma08: `$${results.totalInvestment.toLocaleString()}`,
        difference: `$${results.comparisonToTraditionalTutoring.savings.toLocaleString()}`,
      },
      {
        id: '2',
        metric: 'Expected Grade Improvement',
        traditional: '5-10%',
        sigma08: `${results.expectedGradeImprovement.toFixed(1)}%`,
        difference: `+${(results.expectedGradeImprovement - 7.5).toFixed(1)}%`,
      },
      {
        id: '3',
        metric: 'Cost per Grade Point',
        traditional: '$500-1000',
        sigma08: `$${results.costPerGradePoint.toFixed(0)}`,
        difference: `$${(750 - results.costPerGradePoint).toFixed(0)}`,
      },
      {
        id: '4',
        metric: '5-Year Total Value',
        traditional: 'N/A',
        sigma08: `$${results.fiveYearValue.toLocaleString()}`,
        difference: `+$${results.fiveYearValue.toLocaleString()}`,
      },
    ],
    [results]
  );

  const chartOptions = {
    height: '400px',
    theme: 'g10',
    toolbar: {
      enabled: false,
    },
  };

  const barChartOptions = {
    ...chartOptions,
    title: 'Cost Comparison Analysis',
    axes: {
      left: {
        title: 'Annual Cost ($)',
        mapsTo: 'value',
      },
      bottom: {
        mapsTo: 'group',
        scaleType: 'labels',
      },
    },
    color: {
      scale: {
        'Traditional Tutoring': '#78a9ff',
        'Sigma08 Platform': '#0f62fe',
        Savings: '#0043ce',
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    title: 'Projected ROI Growth (5 Years)',
    axes: {
      bottom: {
        title: 'Timeline',
        mapsTo: 'date',
        scaleType: 'labels',
      },
      left: {
        title: 'Cumulative Value ($)',
        mapsTo: 'value',
      },
    },
    curve: 'curveMonotoneX',
    points: {
      enabled: true,
    },
  };

  const donutChartOptions = {
    ...chartOptions,
    title: 'Value Breakdown',
    donut: {
      center: {
        label: 'Total Value',
        number: results.yearlyValue.toFixed(0),
      },
    },
    legend: {
      alignment: 'center',
    },
  };

  return (
    <div className="roi-visualization">
      <Stack gap={6}>
        {/* Action Bar */}
        <div className="visualization-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <Button kind="tertiary" size="sm" renderIcon={Share} onClick={onShare}>
            Share Results
          </Button>
          <Button kind="tertiary" size="sm" renderIcon={Download} onClick={onExportPDF}>
            Export PDF Report
          </Button>
        </div>

        {/* Key Metrics Summary */}
        <Grid>
          <Column lg={4} md={4} sm={4}>
            <Tile className="metric-tile">
              <h4 className="metric-title">Annual Savings</h4>
              <div className="metric-value" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f62fe' }}>
                ${results.comparisonToTraditionalTutoring.savings.toLocaleString()}
              </div>
              <Tag type="green" size="sm">
                {results.comparisonToTraditionalTutoring.percentageSaved.toFixed(0)}% saved
              </Tag>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile className="metric-tile">
              <h4 className="metric-title">Grade Improvement</h4>
              <div className="metric-value" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f62fe' }}>
                +{results.expectedGradeImprovement.toFixed(1)}%
              </div>
              <Tag type="blue" size="sm">
                Projected increase
              </Tag>
            </Tile>
          </Column>
          <Column lg={4} md={4} sm={4}>
            <Tile className="metric-tile">
              <h4 className="metric-title">5-Year ROI</h4>
              <div className="metric-value" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f62fe' }}>
                ${results.fiveYearValue.toLocaleString()}
              </div>
              <Tag type="purple" size="sm">
                Total value
              </Tag>
            </Tile>
          </Column>
        </Grid>

        {/* Charts */}
        {variant === 'detailed' && (
          <>
            <Grid>
              <Column lg={8} md={8} sm={4}>
                <Tile>
                  <SimpleBarChart data={costComparisonData} options={barChartOptions} />
                </Tile>
              </Column>
              <Column lg={8} md={8} sm={4}>
                <Tile>
                  <LineChart data={roiGrowthData} options={lineChartOptions} />
                </Tile>
              </Column>
            </Grid>

            <Grid>
              <Column lg={8} md={8} sm={4}>
                <Tile>
                  <DonutChart data={valueBreakdownData} options={donutChartOptions} />
                </Tile>
              </Column>
              <Column lg={8} md={8} sm={4}>
                <Tile>
                  <h3 style={{ marginBottom: '1rem' }}>Detailed Comparison</h3>
                  <DataTable
                    rows={comparisonTableData}
                    headers={[
                      { key: 'metric', header: 'Metric' },
                      { key: 'traditional', header: 'Traditional Tutoring' },
                      { key: 'sigma08', header: 'Sigma08 Platform' },
                      { key: 'difference', header: 'Difference' },
                    ]}
                  >
                    {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                      <TableContainer>
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map(header => (
                                <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map(row => (
                              <TableRow {...getRowProps({ row })}>
                                {row.cells.map(cell => (
                                  <TableCell key={cell.id}>{cell.value}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>
                </Tile>
              </Column>
            </Grid>
          </>
        )}

        {/* Success Message */}
        <InlineNotification
          kind="success"
          title="Analysis Complete"
          subtitle="Your ROI calculation shows significant value. Schedule a demo to learn more about implementation."
          actions={
            <Button kind="ghost" size="sm" renderIcon={Analytics}>
              Schedule Demo
            </Button>
          }
        />
      </Stack>
    </div>
  );
};

export default ROIVisualization;
